import { useParams } from "react-router-dom";
import { ActionButton, Dialog, DialogContent } from "@jhool-io/fe-components";
import { useState } from "react";
import { useGetDiagnosisCodesDisplay } from "../../../../hooks/helpers";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import {
    IAddendumDetails,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import { EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS } from "../../../../utils/constants";
import {
    formatDate,
    cn,
    displayNameInRightFormat,
    removeEnumUnderscore,
    convertTimeTo12HoursFormat,
} from "../../../../utils/helpers";
import NoteContentItem from "../NoteContentItem/NoteContentItem";

import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import useGetDiagnosisCodeToDisplay from "../../../../modules/notes/hooks";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import CheckIcon from "../../../../icons/Check";

interface ViewEvaluationManagementAndTherapyNoteMedProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewEvaluationManagementAndTherapyNoteMed({
    noteContent,
    fieldAddendums,
}: ViewEvaluationManagementAndTherapyNoteMedProps) {
    const [docInView, setDocInView] = useState<IViewFile | null>(null);

    const diagonosisCodes = useGetDiagnosisCodesDisplay(
        noteContent.diagnosis_codes
    );

    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const dateOfNextSession = noteContent.date_of_next_session
        ? formatDate(noteContent.date_of_next_session, true)
        : "--";

    const displayCheckedOptions = (values?: string[]) =>
        Array.isArray(values) && values.length > 0 ? (
            <div className={styles.checkboxes}>
                {values.map((value) => (
                    <div className={styles.checkbox} key={value}>
                        <span
                            className={cn(styles.checkbox_wrap, styles.checked)}
                        >
                            <CheckIcon className="stroke-text" />
                        </span>
                        {value}
                    </div>
                ))}
            </div>
        ) : (
            "--"
        );

    const displayBooleanCheckbox = (label: string, isChecked?: boolean) => (
        <div className={styles.checkbox}>
            <span
                className={cn(styles.checkbox_wrap, {
                    [styles.checked]: Boolean(isChecked),
                })}
            >
                <CheckIcon className="stroke-text" />
            </span>
            {label}
        </div>
    );

    const displayEnumValue = (value?: string) =>
        value ? removeEnumUnderscore(value) : "--";

    const displayTime = (value?: string) =>
        value ? convertTimeTo12HoursFormat(value) : "--";

    return (
        <>
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.session_location
                }
                fieldId="session_location"
                addendums={fieldAddendums?.session_location}
                content={
                    noteContent.session_location
                        ? removeEnumUnderscore(noteContent.session_location)
                        : "--"
                }
            />
            {noteContent.session_location === SessionLocations.Telehealth && (
                <>
                    <NoteContentItem
                        headerText={
                            EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.location_of_the_client
                        }
                        fieldId="location_of_the_client"
                        addendums={fieldAddendums?.location_of_the_client}
                        content={
                            noteContent.location_of_the_client
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_client
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_client === "other" && (
                        <NoteContentItem
                            headerText={
                                EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.other_location_of_the_client
                            }
                            fieldId="other_location_of_the_client"
                            addendums={
                                fieldAddendums?.other_location_of_the_client
                            }
                            content={
                                noteContent.other_location_of_the_client || "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.location_of_the_provider
                        }
                        fieldId="location_of_the_provider"
                        addendums={fieldAddendums?.location_of_the_provider}
                        content={
                            noteContent.location_of_the_provider
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_provider
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_provider === "other" && (
                        <NoteContentItem
                            headerText={
                                EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.other_location_of_the_provider
                            }
                            fieldId="other_location_of_the_provider"
                            addendums={
                                fieldAddendums?.other_location_of_the_provider
                            }
                            content={
                                noteContent.other_location_of_the_provider ||
                                "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                        }
                        fieldId="consent_was_received_for_the_teletherapy_session"
                        addendums={
                            fieldAddendums?.consent_was_received_for_the_teletherapy_session
                        }
                        content={displayBooleanCheckbox(
                            EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session,
                            noteContent.consent_was_received_for_the_teletherapy_session
                        )}
                    />
                    <NoteContentItem
                        headerText={
                            EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.the_session_was_conducted_via_video
                        }
                        fieldId="the_session_was_conducted_via_video"
                        addendums={
                            fieldAddendums?.the_session_was_conducted_via_video
                        }
                        content={displayBooleanCheckbox(
                            EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.the_session_was_conducted_via_video,
                            noteContent.the_session_was_conducted_via_video
                        )}
                    />
                </>
            )}

            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.diagnosis_codes
                }
                fieldId="diagnosis_codes"
                addendums={fieldAddendums?.diagnosis_codes}
                content={
                    diagonosisCodes && Array.isArray(diagonosisCodes) ? (
                        <div className={styles.checkboxes}>
                            {diagonosisCodes.map((code) => (
                                <div className={styles.checkbox} key={code}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: true,
                                        })}
                                    >
                                        <CheckIcon className="stroke-text" />
                                    </span>
                                    {code}
                                </div>
                            ))}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.secondary_diagnosis
                }
                fieldId="secondary_diagnosis"
                addendums={fieldAddendums?.secondary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.secondary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.tertiary_diagnosis
                }
                fieldId="tertiary_diagnosis"
                addendums={fieldAddendums?.tertiary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.tertiary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.additional_diagnosis_one
                }
                fieldId="additional_diagnosis_one"
                addendums={fieldAddendums?.additional_diagnosis_one}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_one
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.additional_diagnosis_two
                }
                fieldId="additional_diagnosis_two"
                addendums={fieldAddendums?.additional_diagnosis_two}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_two
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.interval_history
                }
                fieldId="interval_history"
                addendums={fieldAddendums?.interval_history}
                content={noteContent.interval_history || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.interval_psychiatric_assessment_mse
                }
                fieldId="interval_psychiatric_assessment_mse"
                addendums={fieldAddendums?.interval_psychiatric_assessment_mse}
                content={
                    noteContent.interval_psychiatric_assessment_mse || "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.patient_ability_and_capability_to_respond_to_treatment
                }
                fieldId="patient_ability_and_capability_to_respond_to_treatment"
                addendums={
                    fieldAddendums?.patient_ability_and_capability_to_respond_to_treatment
                }
                content={displayCheckedOptions(
                    noteContent.patient_ability_and_capability_to_respond_to_treatment
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                }
                fieldId="diminished_poor_or_limited_quality_patient_oriented_evidence_details"
                addendums={
                    fieldAddendums?.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                }
                content={
                    noteContent.diminished_poor_or_limited_quality_patient_oriented_evidence_details ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.condition_type
                }
                fieldId="condition_type"
                addendums={fieldAddendums?.condition_type}
                content={displayEnumValue(noteContent.condition_type)}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.condition_type_additional_details
                }
                fieldId="condition_type_additional_details"
                addendums={fieldAddendums?.condition_type_additional_details}
                content={noteContent.condition_type_additional_details || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.condition_status
                }
                fieldId="condition_status"
                addendums={fieldAddendums?.condition_status}
                content={displayEnumValue(noteContent.condition_status)}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.condition_status_additional_details
                }
                fieldId="condition_status_additional_details"
                addendums={fieldAddendums?.condition_status_additional_details}
                content={
                    noteContent.condition_status_additional_details || "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.current_medications_and_medications_changes
                }
                fieldId="current_medications_and_medications_changes"
                addendums={
                    fieldAddendums?.current_medications_and_medications_changes
                }
                content={
                    noteContent.current_medications_and_medications_changes ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.no_side_effects_or_adverse_reactions_noted_or_reported
                }
                fieldId="no_side_effects_or_adverse_reactions_noted_or_reported"
                addendums={
                    fieldAddendums?.no_side_effects_or_adverse_reactions_noted_or_reported
                }
                content={displayBooleanCheckbox(
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.no_side_effects_or_adverse_reactions_noted_or_reported,
                    noteContent.no_side_effects_or_adverse_reactions_noted_or_reported
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.lab_tests
                }
                fieldId="lab_tests"
                addendums={fieldAddendums?.lab_tests}
                content={displayCheckedOptions(noteContent.lab_tests)}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.lab_tests_details
                }
                fieldId="lab_tests_details"
                addendums={fieldAddendums?.lab_tests_details}
                content={noteContent.lab_tests_details || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.additional_documentation_review_internal
                }
                fieldId="additional_documentation_review_internal"
                addendums={
                    fieldAddendums?.additional_documentation_review_internal
                }
                content={
                    noteContent.additional_documentation_review_internal || "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.additional_documentation_review_external
                }
                fieldId="additional_documentation_review_external"
                addendums={
                    fieldAddendums?.additional_documentation_review_external
                }
                content={
                    noteContent.additional_documentation_review_external || "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.decision_regarding_hospitalization_or_patient_family_decision_to_forego_hospitalization_upon_shared_decision_making
                }
                fieldId="decision_regarding_hospitalization_or_patient_family_decision_to_forego_hospitalization_upon_shared_decision_making"
                addendums={
                    fieldAddendums?.decision_regarding_hospitalization_or_patient_family_decision_to_forego_hospitalization_upon_shared_decision_making
                }
                content={
                    noteContent.decision_regarding_hospitalization_or_patient_family_decision_to_forego_hospitalization_upon_shared_decision_making ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.social_factors_which_may_impair_or_prevent_patients_from_receiving_care_and_or_treatment
                }
                fieldId="social_factors_which_may_impair_or_prevent_patients_from_receiving_care_and_or_treatment"
                addendums={
                    fieldAddendums?.social_factors_which_may_impair_or_prevent_patients_from_receiving_care_and_or_treatment
                }
                content={
                    noteContent.social_factors_which_may_impair_or_prevent_patients_from_receiving_care_and_or_treatment ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.independent_interpretation_of_tests
                }
                fieldId="independent_interpretation_of_tests"
                addendums={fieldAddendums?.independent_interpretation_of_tests}
                content={
                    noteContent.independent_interpretation_of_tests || "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.discussion_of_tests_or_management_with_external_professionals
                }
                fieldId="discussion_of_tests_or_management_with_external_professionals"
                addendums={
                    fieldAddendums?.discussion_of_tests_or_management_with_external_professionals
                }
                content={
                    noteContent.discussion_of_tests_or_management_with_external_professionals ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.use_of_an_independent_historian
                }
                fieldId="use_of_an_independent_historian"
                addendums={fieldAddendums?.use_of_an_independent_historian}
                content={noteContent.use_of_an_independent_historian || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.treatment_goal_one
                }
                fieldId="treatment_goal_one"
                addendums={fieldAddendums?.treatment_goal_one}
                content={noteContent.treatment_goal_one || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.treatment_goal_two
                }
                fieldId="treatment_goal_two"
                addendums={fieldAddendums?.treatment_goal_two}
                content={noteContent.treatment_goal_two || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.treatment_goal_three
                }
                fieldId="treatment_goal_three"
                addendums={fieldAddendums?.treatment_goal_three}
                content={noteContent.treatment_goal_three || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.progress_one
                }
                fieldId="progress_one"
                addendums={fieldAddendums?.progress_one}
                content={noteContent.progress_one || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.progress_two
                }
                fieldId="progress_two"
                addendums={fieldAddendums?.progress_two}
                content={noteContent.progress_two || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.progress_three
                }
                fieldId="progress_three"
                addendums={fieldAddendums?.progress_three}
                content={noteContent.progress_three || "--"}
            />

            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.frequency_of_sessions
                }
                fieldId="frequency_of_sessions"
                addendums={fieldAddendums?.frequency_of_sessions}
                content={displayCheckedOptions(
                    noteContent.frequency_of_sessions
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.other_frequency_of_sessions
                }
                fieldId="other_frequency_of_sessions"
                addendums={fieldAddendums?.other_frequency_of_sessions}
                content={noteContent.other_frequency_of_sessions || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.date_of_next_session
                }
                fieldId="date_of_next_session"
                addendums={fieldAddendums?.date_of_next_session}
                content={dateOfNextSession}
            />

            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.communication_factors_impacting_treatment
                }
                fieldId="communication_factors_impacting_treatment"
                addendums={
                    fieldAddendums?.communication_factors_impacting_treatment
                }
                content={displayCheckedOptions(
                    noteContent.communication_factors_impacting_treatment
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.details_around_communication_factors_impacting_treatment
                }
                fieldId="details_around_communication_factors_impacting_treatment"
                addendums={
                    fieldAddendums?.details_around_communication_factors_impacting_treatment
                }
                content={
                    noteContent.details_around_communication_factors_impacting_treatment ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.files
                }
                fieldId="files"
                addendums={fieldAddendums?.files}
                content={
                    <div>
                        {data?.data?.note_content_documents &&
                        data?.data?.note_content_documents.length ? (
                            <div className="flex gap-[var(--spacing-items)] flex-wrap">
                                {data?.data?.note_content_documents.map(
                                    (doc) => (
                                        <ActionButton
                                            key={doc.note_content_document_id}
                                            variant="can-hover"
                                            size="auto"
                                            className="h-7 !text-text text-caption underline"
                                            actionTitle="View file"
                                            onClick={() => {
                                                setDocInView(doc);
                                            }}
                                        >
                                            <span>{`${displayNameInRightFormat(
                                                {
                                                    firstName:
                                                        doc.uploaded_by
                                                            .first_name,
                                                    lastName:
                                                        doc.uploaded_by
                                                            .last_name,
                                                },
                                                false
                                            )} -  ${doc.document_name}`}</span>
                                        </ActionButton>
                                    )
                                )}
                            </div>
                        ) : (
                            "--"
                        )}
                    </div>
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.medication_management_duration
                }
                fieldId="medication_management_duration"
                addendums={fieldAddendums?.medication_management_duration}
                content={
                    noteContent.medication_management_duration
                        ? `${noteContent.medication_management_duration} minutes`
                        : "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.narrative_describing_in_detail_the_issues_reviewed_with_patient
                }
                fieldId="narrative_describing_in_detail_the_issues_reviewed_with_patient"
                addendums={
                    fieldAddendums?.narrative_describing_in_detail_the_issues_reviewed_with_patient
                }
                content={
                    noteContent.narrative_describing_in_detail_the_issues_reviewed_with_patient ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.counseling_provided_with_patient_family_caregiver
                }
                fieldId="counseling_provided_with_patient_family_caregiver"
                addendums={
                    fieldAddendums?.counseling_provided_with_patient_family_caregiver
                }
                content={displayCheckedOptions(
                    noteContent.counseling_provided_with_patient_family_caregiver
                )}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.type_of_therapy_performed
                }
                fieldId="type_of_therapy_performed"
                addendums={fieldAddendums?.type_of_therapy_performed}
                content={noteContent.type_of_therapy_performed || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.recommendations_and_plan
                }
                fieldId="recommendations_and_plan"
                addendums={fieldAddendums?.recommendations_and_plan}
                content={noteContent.recommendations_and_plan || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.prognosis
                }
                fieldId="prognosis"
                addendums={fieldAddendums?.prognosis}
                content={noteContent.prognosis || "--"}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.modality
                }
                fieldId="modality"
                addendums={fieldAddendums?.modality}
                content={displayEnumValue(noteContent.modality)}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.start_time_of_counseling
                }
                fieldId="start_time_of_counseling"
                addendums={fieldAddendums?.start_time_of_counseling}
                content={displayTime(noteContent.start_time_of_counseling)}
            />
            <NoteContentItem
                headerText={
                    EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS.end_time_of_counseling
                }
                fieldId="end_time_of_counseling"
                addendums={fieldAddendums?.end_time_of_counseling}
                content={displayTime(noteContent.end_time_of_counseling)}
            />
        </>
    );
}
